/**
 * Cover & Metadata Harvest + Pre-Parse Snapshot — dedicated admin page.
 *
 * v1.3.57 — moved here from the per-row controls on the Playlists table
 * (which had grown to dominate the table horizontally and made the rest
 * of the playlist metadata hard to scan). One Card per playlist; each
 * Card has both engines (B2C metadata harvest + M3U pre-parse snapshot)
 * AND the per-provider cover-cache inspector that used to live in the
 * Actions column.
 *
 * Data flow is identical to Playlists.jsx — uses the same admin REST
 * endpoints, just on a different page. The Playlists table stays the
 * canonical source of truth for the row data.
 */
import React, { useEffect, useState } from "react";
import {
  listPlaylists,
  getCoverStats,
  refreshCovers,
  setPlaylistB2cEnabled,
  getPlaylistHarvestStatus,
  runPlaylistHarvest,
  deletePlaylistSnapshot,
  setPlaylistSnapshotEnabled,
  getPlaylistSnapshotStatus,
  runPlaylistSnapshot,
  deletePlaylistPreparsedSnapshot,
  setPlaylistB2cAutoHarvest,
  setPlaylistSnapshotAutoRun,
  setSnapshotRequiredOnStart,
  setDisableSeriesPreload,
  setPreferM3uSource,
} from "../lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  ListVideo,
  Image as ImageIcon,
  RefreshCw,
  Database,
  PlayCircle,
} from "lucide-react";
import CoverInspectorDialog from "./CoverInspectorDialog";

export default function MetadataAndSnapshot() {
  const { toast } = useToast();
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coverStats, setCoverStats] = useState(null);
  const [coverBusy, setCoverBusy] = useState({});
  const [inspecting, setInspecting] = useState(null);

  const [harvestStatus, setHarvestStatus] = useState({});
  const [harvestBusy, setHarvestBusy] = useState({});
  const [snapStatus, setSnapStatus] = useState({});
  const [snapBusy, setSnapBusy] = useState({});

  // ---------------------------------------------------------------- load
  async function reload() {
    try {
      const data = await listPlaylists();
      setPlaylists(data || []);
    } catch (e) {
      toast({
        title: "Couldn't load playlists",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }
  async function reloadCoverStats() {
    try {
      setCoverStats(await getCoverStats());
    } catch { /* non-fatal */ }
  }
  async function refreshHarvestStatus(id) {
    try {
      const data = await getPlaylistHarvestStatus(id);
      setHarvestStatus((s) => ({ ...s, [id]: data }));
    } catch { /* ignore */ }
  }
  async function refreshSnapStatus(id) {
    try {
      const data = await getPlaylistSnapshotStatus(id);
      setSnapStatus((s) => ({ ...s, [id]: data }));
    } catch { /* ignore */ }
  }

  useEffect(() => {
    reload();
    reloadCoverStats();
    const id = setInterval(reloadCoverStats, 5000);
    return () => clearInterval(id);
  }, []);

  // Pull statuses for every playlist once we know the list.
  useEffect(() => {
    for (const p of playlists) {
      if (p.b2c_url) refreshHarvestStatus(p.id);
      if (p.m3u_url && p.m3u_url.startsWith("http")) refreshSnapStatus(p.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlists.length]);

  // ---------------------------------------------------------------- covers
  async function handleRefreshCovers(playlistId) {
    setCoverBusy((b) => ({ ...b, [playlistId]: true }));
    try {
      await refreshCovers(playlistId, false);
      toast({
        title: "Cover harvest started",
        description:
          "Walking the catalog and downloading every cover. Stats refresh every 5 s while it runs.",
      });
    } catch (e) {
      toast({
        title: "Couldn't start cover refresh",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setCoverBusy((b) => ({ ...b, [playlistId]: false }));
    }
  }

  // ---------------------------------------------------------------- B2C
  async function handleToggleB2cEnabled(p, next) {
    setPlaylists((prev) =>
      prev.map((x) => (x.id === p.id ? { ...x, b2c_enabled: !!next } : x)),
    );
    try {
      await setPlaylistB2cEnabled(p.id, !!next);
      toast({
        title: next ? "Harvest enabled" : "Harvest disabled",
      });
      refreshHarvestStatus(p.id);
    } catch (e) {
      setPlaylists((prev) =>
        prev.map((x) => (x.id === p.id ? { ...x, b2c_enabled: !next } : x)),
      );
      toast({
        title: "Toggle failed",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  }
  async function handleRunHarvest(p) {
    setHarvestBusy((b) => ({ ...b, [p.id]: true }));
    try { await setPlaylistB2cEnabled(p.id, true); } catch {/* ignore */}
    setPlaylists((prev) =>
      prev.map((x) => (x.id === p.id ? { ...x, b2c_enabled: true } : x)),
    );
    try {
      await runPlaylistHarvest(p.id);
      toast({
        title: "Harvest started",
        description: `Server is fetching the catalog for "${p.name}". This usually takes 30-90 s.`,
      });
      let polls = 0;
      const tick = async () => {
        polls += 1;
        await refreshHarvestStatus(p.id);
        const st = harvestStatus[p.id];
        if (polls < 36 && (st?.running || !st?.snapshot)) setTimeout(tick, 5000);
        else setHarvestBusy((b) => ({ ...b, [p.id]: false }));
      };
      setTimeout(tick, 3000);
    } catch (e) {
      setHarvestBusy((b) => ({ ...b, [p.id]: false }));
      toast({
        title: "Couldn't start harvest",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  }
  async function handleDeleteSnapshot(p) {
    try {
      await deletePlaylistSnapshot(p.id);
      toast({ title: "Snapshot deleted" });
      refreshHarvestStatus(p.id);
    } catch (e) {
      toast({
        title: "Delete failed",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  }

  // ---------------------------------------------------------------- snapshot
  async function handleToggleSnapshotEnabled(p, next) {
    setPlaylists((prev) =>
      prev.map((x) =>
        x.id === p.id ? { ...x, playlist_snapshot_enabled: !!next } : x,
      ),
    );
    try {
      await setPlaylistSnapshotEnabled(p.id, !!next);
      toast({
        title: next ? "Pre-parse snapshot enabled" : "Pre-parse snapshot disabled",
      });
      refreshSnapStatus(p.id);
    } catch (e) {
      setPlaylists((prev) =>
        prev.map((x) =>
          x.id === p.id ? { ...x, playlist_snapshot_enabled: !next } : x,
        ),
      );
      toast({
        title: "Toggle failed",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  }
  async function handleRunSnapshot(p) {
    setSnapBusy((b) => ({ ...b, [p.id]: true }));
    try { await setPlaylistSnapshotEnabled(p.id, true); } catch {/* ignore */}
    setPlaylists((prev) =>
      prev.map((x) =>
        x.id === p.id ? { ...x, playlist_snapshot_enabled: true } : x,
      ),
    );
    try {
      await runPlaylistSnapshot(p.id);
      toast({
        title: "Pre-parse started",
        description: `Server is downloading and parsing "${p.name}". 1-3 min for huge catalogs.`,
      });
      let polls = 0;
      const tick = async () => {
        polls += 1;
        await refreshSnapStatus(p.id);
        const st = snapStatus[p.id];
        if (polls < 60 && (st?.running || !st?.snapshot)) setTimeout(tick, 5000);
        else setSnapBusy((b) => ({ ...b, [p.id]: false }));
      };
      setTimeout(tick, 3000);
    } catch (e) {
      setSnapBusy((b) => ({ ...b, [p.id]: false }));
      toast({
        title: "Couldn't start pre-parse",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  }
  async function handleDeletePreparsedSnapshot(p) {
    try {
      await deletePlaylistPreparsedSnapshot(p.id);
      toast({ title: "Pre-parsed snapshot deleted" });
      refreshSnapStatus(p.id);
    } catch (e) {
      toast({
        title: "Delete failed",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  }

  // v1.3.58 — auto-toggle handlers. Same optimistic-update + revert
  // pattern as the existing toggles above. We patch the row in place
  // so the button re-renders immediately; the backend write is the
  // source of truth.
  async function handleToggleB2cAuto(p, next) {
    setPlaylists((prev) =>
      prev.map((x) => (x.id === p.id ? { ...x, b2c_auto_harvest: !!next } : x)),
    );
    try {
      await setPlaylistB2cAutoHarvest(p.id, !!next);
      toast({
        title: next
          ? "Auto-harvest enabled"
          : "Auto-harvest disabled",
        description: next
          ? "Server will refresh this playlist's metadata snapshot whenever it goes stale (≈ every 24 h)."
          : 'Server will never auto-refresh this snapshot. Use "Run now" to rebuild manually.',
      });
    } catch (e) {
      setPlaylists((prev) =>
        prev.map((x) => (x.id === p.id ? { ...x, b2c_auto_harvest: !next } : x)),
      );
      toast({
        title: "Toggle failed",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  }
  async function handleToggleSnapshotAuto(p, next) {
    setPlaylists((prev) =>
      prev.map((x) =>
        x.id === p.id ? { ...x, playlist_snapshot_auto_run: !!next } : x,
      ),
    );
    try {
      await setPlaylistSnapshotAutoRun(p.id, !!next);
      toast({
        title: next
          ? "Auto pre-parse enabled"
          : "Auto pre-parse disabled",
      });
    } catch (e) {
      setPlaylists((prev) =>
        prev.map((x) =>
          x.id === p.id ? { ...x, playlist_snapshot_auto_run: !next } : x,
        ),
      );
      toast({
        title: "Toggle failed",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  }
  async function handleToggleSnapshotRequiredOnStart(p, next) {
    setPlaylists((prev) =>
      prev.map((x) =>
        x.id === p.id ? { ...x, snapshot_required_on_start: !!next } : x,
      ),
    );
    try {
      await setSnapshotRequiredOnStart(p.id, !!next);
      toast({
        title: next
          ? "Snapshot-on-start enabled"
          : "Snapshot-on-start disabled",
        description: next
          ? "Devices will fetch the pre-parsed snapshot first when opening the app and show an Updating… pill while it loads. Falls back to the provider if the snapshot is unavailable."
          : "Devices will fetch the provider's raw M3U on every cold start as before.",
      });
    } catch (e) {
      setPlaylists((prev) =>
        prev.map((x) =>
          x.id === p.id ? { ...x, snapshot_required_on_start: !next } : x,
        ),
      );
      toast({
        title: "Toggle failed",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  }

  // v1.4.27 — Toggle handler for "Prefer M3U source (legacy)".
  async function handleTogglePreferM3uSource(p, next) {
    setPlaylists((prev) =>
      prev.map((x) =>
        x.id === p.id ? { ...x, prefer_m3u_source: !!next } : x,
      ),
    );
    try {
      await setPreferM3uSource(p.id, !!next);
      toast({
        title: next
          ? "M3U source preferred (legacy)"
          : "Xtream API preferred (default)",
        description: next
          ? "Devices will download and parse the raw M3U file on cold start (legacy flow). Slower on huge catalogs but useful when a provider's Xtream JSON API is broken."
          : "Devices will build the channel list directly from the Xtream JSON API (get_live_streams, get_vod_streams, get_series). Faster and more complete than the M3U parse.",
      });
    } catch (e) {
      setPlaylists((prev) =>
        prev.map((x) =>
          x.id === p.id ? { ...x, prefer_m3u_source: !next } : x,
        ),
      );
      toast({
        title: "Toggle failed",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  }

  // v1.4.26 — Toggle handler for "Disable series pre-loading" — the
  // last remaining OOM guard for 400k-row Xtream catalogs on Smart TVs.
  // last remaining OOM guard for 400k-row Xtream catalogs on Smart TVs.
  async function handleToggleDisableSeriesPreload(p, next) {
    setPlaylists((prev) =>
      prev.map((x) =>
        x.id === p.id ? { ...x, disable_series_preload: !!next } : x,
      ),
    );
    try {
      await setDisableSeriesPreload(p.id, !!next);
      toast({
        title: next
          ? "Series pre-loading disabled"
          : "Series pre-loading enabled",
        description: next
          ? "Devices will skip the Xtream get_series harvest on cold start. Recommended for playlists >200k rows on Smart TVs. Trade-off: series that only exist in the Xtream API (not the M3U) won't appear."
          : "Devices will merge the Xtream get_series catalog into the series library on cold start.",
      });
    } catch (e) {
      setPlaylists((prev) =>
        prev.map((x) =>
          x.id === p.id ? { ...x, disable_series_preload: !next } : x,
        ),
      );
      toast({
        title: "Toggle failed",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  }

  // ---------------------------------------------------------------- render
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
          <Database className="w-5 h-5 text-orange-500" />
          Cover &amp; Metadata Harvest and Snapshot
        </h1>
        <p className="text-sm text-slate-600 mt-1 max-w-3xl">
          Per-playlist server-side pre-processing. The <strong>Cover &amp; Metadata Harvest</strong>
          {" "}walks the B2C catalog and caches posters / titles / descriptions
          so devices don&apos;t have to. The <strong>Pre-Parse Snapshot</strong>
          {" "}fetches the M3U on the server, parses it once, and ships the
          compact result to every device — cuts cold-start time on huge
          (500k+) catalogs from minutes to seconds. <em>Image cache stats</em>
          {" "}for each provider live at the bottom of every card.
        </p>
      </div>

      {loading ? (
        <Card>
          <CardContent className="p-8 text-center text-slate-500">
            Loading playlists…
          </CardContent>
        </Card>
      ) : playlists.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-slate-500">
            <ListVideo className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p>No playlists yet. Add one on the Playlists page first.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {playlists.map((p) => (
            <PlaylistCard
              key={p.id}
              p={p}
              harvestStatus={harvestStatus[p.id]}
              harvestBusy={!!harvestBusy[p.id]}
              snapStatus={snapStatus[p.id]}
              snapBusy={!!snapBusy[p.id]}
              coverStats={coverStats}
              coverBusy={!!coverBusy[p.id]}
              onToggleB2c={handleToggleB2cEnabled}
              onToggleB2cAuto={handleToggleB2cAuto}
              onRunHarvest={handleRunHarvest}
              onDeleteSnapshot={handleDeleteSnapshot}
              onToggleSnapshot={handleToggleSnapshotEnabled}
              onToggleSnapshotAuto={handleToggleSnapshotAuto}
              onToggleSnapshotRequiredOnStart={handleToggleSnapshotRequiredOnStart}
              onToggleDisableSeriesPreload={handleToggleDisableSeriesPreload}
              onTogglePreferM3uSource={handleTogglePreferM3uSource}
              onRunSnapshot={handleRunSnapshot}
              onDeletePreparsedSnapshot={handleDeletePreparsedSnapshot}
              onRefreshCovers={handleRefreshCovers}
              onInspectCovers={() => setInspecting(p)}
            />
          ))}
        </div>
      )}

      <CoverInspectorDialog
        playlist={inspecting}
        onClose={() => setInspecting(null)}
      />
    </div>
  );
}

// ============================================================== card
function PlaylistCard({
  p,
  harvestStatus,
  harvestBusy,
  snapStatus,
  snapBusy,
  coverStats,
  coverBusy,
  onToggleB2c,
  onToggleB2cAuto,
  onRunHarvest,
  onDeleteSnapshot,
  onToggleSnapshot,
  onToggleSnapshotAuto,
  onToggleSnapshotRequiredOnStart,
  onToggleDisableSeriesPreload,
  onTogglePreferM3uSource,
  onRunSnapshot,
  onDeletePreparsedSnapshot,
  onRefreshCovers,
  onInspectCovers,
}) {
  const ps = (coverStats?.per_playlist || []).find(
    (x) => x.playlist_id === p.id,
  );
  const cached = ps?.cached || 0;
  const failed = ps?.failed || 0;
  const mbCov = Math.round((ps?.bytes || 0) / 1024 / 1024);
  const host = ps?.provider_host || "—";
  const shared = ps?.shared;

  return (
    <Card
      className="border-slate-200"
      data-testid={`metadata-card-${p.id}`}
    >
      <CardContent className="p-5 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-base font-semibold text-slate-800">
              {p.name}
            </div>
            <div className="text-[11px] text-slate-500 font-mono truncate max-w-md mt-0.5" title={p.m3u_url}>
              {p.m3u_url}
            </div>
          </div>
          {p.last_requested_at ? (
            <span className="text-[10px] text-slate-400 shrink-0">
              last used{" "}
              {(() => {
                try { return new Date(p.last_requested_at).toLocaleDateString(); }
                catch { return ""; }
              })()}
            </span>
          ) : (
            <span className="text-[10px] text-slate-400 italic shrink-0">never used</span>
          )}
        </div>

        {/* ------- Cover & Metadata Harvest ------- */}
        {p.b2c_url ? (
          <Section
            title="Cover & Metadata Harvest"
            accent="emerald"
            on={p.b2c_enabled !== false}
            busy={harvestBusy}
            onToggle={(v) => onToggleB2c(p, v)}
            onRun={() => onRunHarvest(p)}
            toggleTestId={`admin-playlist-b2c-enabled-${p.id}`}
            runTestId={`admin-playlist-run-harvest-${p.id}`}
            autoOn={p.b2c_auto_harvest !== false}
            onToggleAuto={(v) => onToggleB2cAuto(p, v)}
            autoTestId={`admin-playlist-b2c-auto-${p.id}`}
            autoTitleOn="Auto-run scheduled — server rebuilds this snapshot every ~24 h whenever it's stale. Click to disable."
            autoTitleOff="No auto-run — admin must press Run now to rebuild. Click to enable scheduled refresh."
            warning={
              p.b2c_enabled === false && harvestStatus?.snapshot
                ? "Snapshot exists but disabled — devices won't use it"
                : null
            }
            status={(() => {
              const snap = harvestStatus?.snapshot;
              if (!snap) return { kind: "empty", text: "no snapshot yet" };
              const when = (() => {
                try { return new Date(snap.created_at).toLocaleString(); }
                catch { return snap.created_at; }
              })();
              const mb = Math.max(1, Math.round((snap.size_bytes || 0) / 1024 / 1024));
              return {
                kind: "ready",
                text: `✓ ${snap.item_count?.toLocaleString?.() ?? snap.item_count} entries · ${mb} MB`,
                tooltip: `Click to delete. Last harvest: ${when}`,
                onClick: () => onDeleteSnapshot(p),
              };
            })()}
            statusTestId={`admin-playlist-harvest-status-${p.id}`}
          />
        ) : (
          <DisabledSection
            title="Cover & Metadata Harvest"
            reason="This playlist has no B2C URL. Add B2C credentials on the Playlists page to enable metadata harvest."
          />
        )}

        {/* ------- Pre-Parse Snapshot ------- */}
        {p.m3u_url && p.m3u_url.startsWith("http") ? (
          <Section
            title="Pre-Parse Snapshot"
            accent="sky"
            on={p.playlist_snapshot_enabled !== false}
            busy={snapBusy}
            onToggle={(v) => onToggleSnapshot(p, v)}
            onRun={() => onRunSnapshot(p)}
            toggleTestId={`admin-playlist-snapshot-enabled-${p.id}`}
            runTestId={`admin-playlist-run-snapshot-${p.id}`}
            autoOn={p.playlist_snapshot_auto_run !== false}
            onToggleAuto={(v) => onToggleSnapshotAuto(p, v)}
            autoTestId={`admin-playlist-snapshot-auto-${p.id}`}
            autoTitleOn="Auto-run scheduled — server rebuilds this snapshot every ~24 h whenever it's stale. Click to disable."
            autoTitleOff="No auto-run — admin must press Run now. Click to enable scheduled refresh."
            warning={
              p.playlist_snapshot_enabled === false && snapStatus?.snapshot
                ? "Snapshot exists but disabled — devices won't use it"
                : null
            }
            status={(() => {
              const snap = snapStatus?.snapshot;
              if (!snap) return { kind: "empty", text: "no snapshot yet" };
              const when = (() => {
                try { return new Date(snap.created_at).toLocaleString(); }
                catch { return snap.created_at; }
              })();
              const mb = Math.max(1, Math.round((snap.size_bytes || 0) / 1024 / 1024));
              return {
                kind: "ready",
                text: `✓ ${snap.channel_count?.toLocaleString?.() ?? snap.channel_count} channels · ${mb} MB gz`,
                tooltip: `Click to delete. Last parse: ${when}`,
                onClick: () => onDeletePreparsedSnapshot(p),
              };
            })()}
            statusTestId={`admin-playlist-snapshot-status-${p.id}`}
            extraRow={
              <>
                <SnapshotOnStartToggle
                  on={!!p.snapshot_required_on_start}
                  onToggle={(v) => onToggleSnapshotRequiredOnStart(p, v)}
                  testId={`admin-playlist-snapshot-on-start-${p.id}`}
                />
                {/* v1.4.26 — huge-catalog OOM guard for Smart TVs. */}
                <DisableSeriesPreloadToggle
                  on={!!p.disable_series_preload}
                  onToggle={(v) => onToggleDisableSeriesPreload(p, v)}
                  testId={`admin-playlist-disable-series-preload-${p.id}`}
                />
                {/* v1.4.27 — Legacy M3U-first opt-out (default OFF → Xtream API is primary). */}
                <PreferM3uSourceToggle
                  on={!!p.prefer_m3u_source}
                  onToggle={(v) => onTogglePreferM3uSource(p, v)}
                  testId={`admin-playlist-prefer-m3u-${p.id}`}
                />
              </>
            }
          />
        ) : (
          <DisabledSection
            title="Pre-Parse Snapshot"
            reason="Pre-parse only runs on HTTP M3U URLs. Static / file:// playlists are loaded inline by the device."
          />
        )}

        {/* ------- Image cache ------- */}
        <div className="rounded-md border border-slate-200 bg-slate-50/50 p-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
            <ImageIcon className="w-3.5 h-3.5" />
            Image Cache
          </div>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <button
              type="button"
              onClick={onInspectCovers}
              data-testid={`playlist-cover-stats-${p.id}`}
              className="text-xs text-slate-600 hover:text-emerald-700 hover:bg-emerald-50 px-2 py-1 rounded transition-colors flex items-center gap-1.5"
              title={
                cached
                  ? `${cached} covers cached for provider ${host} (${failed} failed, ${mbCov} MB on disk) — click to inspect`
                  : `No covers cached yet for provider ${host}. Click to inspect.`
              }
            >
              <ImageIcon className="w-3.5 h-3.5" />
              {cached
                ? <span><strong>{cached.toLocaleString()}</strong> cached</span>
                : <span className="text-slate-400">no covers cached yet</span>}
              {failed ? <span className="text-red-500">· {failed} failed</span> : null}
              {mbCov ? <span className="text-slate-400">· {mbCov} MB</span> : null}
              {shared ? (
                <span
                  className="text-[10px] font-medium text-emerald-700 bg-emerald-100 rounded px-1"
                  title="Cache is shared across every playlist on this provider"
                >
                  shared
                </span>
              ) : null}
            </button>
            <Button
              size="sm"
              variant="outline"
              data-testid={`btn-refresh-covers-${p.id}`}
              disabled={coverBusy}
              onClick={() => onRefreshCovers(p.id)}
              className="text-xs"
              title="Walk the catalog & cache every cover"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${coverBusy ? "animate-spin" : ""}`} />
              Refresh covers
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================== section
function Section({
  title,
  accent,
  on,
  busy,
  onToggle,
  onRun,
  toggleTestId,
  runTestId,
  // v1.3.58 — Auto-Run gate
  autoOn,
  onToggleAuto,
  autoTestId,
  autoTitleOn,
  autoTitleOff,
  warning,
  status,
  statusTestId,
  // v1.3.58 — optional extra row rendered under the status line (e.g.
  // the "Use snapshot on app start" toggle for the Pre-Parse section).
  extraRow,
}) {
  // Tailwind needs full class names at build time — explicit map keeps the
  // JIT compiler happy. Adjust here if a new accent colour is needed.
  const accentMap = {
    emerald: {
      onBg: "bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600",
      runBg: "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200",
      statusReady: "text-emerald-700 hover:text-red-600",
      autoOnBg: "bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200",
    },
    sky: {
      onBg: "bg-sky-500 text-white border-sky-600 hover:bg-sky-600",
      runBg: "bg-sky-50 text-sky-700 hover:bg-sky-100 border-sky-200",
      statusReady: "text-sky-700 hover:text-red-600",
      autoOnBg: "bg-sky-100 text-sky-800 border-sky-300 hover:bg-sky-200",
    },
  };
  const colors = accentMap[accent] || accentMap.emerald;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
        <PlayCircle className="w-3.5 h-3.5" />
        {title}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          data-testid={toggleTestId}
          onClick={() => onToggle(!on)}
          className={
            "text-[11px] font-bold px-3 py-1.5 rounded-md border-2 min-w-[112px] transition-colors " +
            (on
              ? colors.onBg
              : "bg-red-100 text-red-800 border-red-400 hover:bg-red-200")
          }
          title={
            on
              ? "ENABLED — devices use this snapshot. Click to disable."
              : "DISABLED — devices ignore this snapshot. Click to enable."
          }
        >
          {on ? "✓ ENABLED" : "✗ DISABLED"}
        </button>
        {/* v1.3.58 — Auto-Run gate. Separate from the device-side
            ENABLED flag so admins can keep a snapshot live for devices
            but freeze its content (e.g. while debugging a provider
            issue). */}
        {onToggleAuto ? (
          <button
            type="button"
            data-testid={autoTestId}
            onClick={() => onToggleAuto(!autoOn)}
            className={
              "text-[10px] font-bold px-2.5 py-1.5 rounded-md border-2 min-w-[88px] transition-colors " +
              (autoOn
                ? colors.autoOnBg
                : "bg-slate-100 text-slate-600 border-slate-300 hover:bg-slate-200")
            }
            title={autoOn ? autoTitleOn : autoTitleOff}
          >
            {autoOn ? "⟳ AUTO ON" : "⏸ AUTO OFF"}
          </button>
        ) : null}
        <button
          type="button"
          onClick={onRun}
          disabled={busy}
          data-testid={runTestId}
          className={
            "text-xs px-3 py-1.5 rounded-md border disabled:opacity-50 disabled:cursor-not-allowed transition-colors " +
            colors.runBg
          }
        >
          {busy ? "Running…" : "Run now"}
        </button>
        {status?.kind === "ready" ? (
          <button
            type="button"
            onClick={status.onClick}
            title={status.tooltip}
            data-testid={statusTestId}
            className={
              "text-[11px] cursor-pointer transition-colors ml-auto " +
              colors.statusReady
            }
          >
            {status.text}
          </button>
        ) : (
          <span
            className="text-[11px] text-slate-400 italic ml-auto"
            data-testid={statusTestId}
          >
            {status?.text || "no snapshot yet"}
          </span>
        )}
      </div>
      {warning ? (
        <div className="mt-2 text-[10px] text-red-600 font-medium bg-red-50 border border-red-200 rounded px-2 py-1 inline-block">
          ⚠ {warning}
        </div>
      ) : null}
      {extraRow ? <div className="mt-2">{extraRow}</div> : null}
    </div>
  );
}

function SnapshotOnStartToggle({ on, onToggle, testId }) {
  return (
    <div className="flex items-start gap-3 p-2.5 rounded-md bg-sky-50/60 border border-sky-200">
      <button
        type="button"
        data-testid={testId}
        onClick={() => onToggle(!on)}
        className={
          "text-[10px] font-bold px-2.5 py-1.5 rounded-md border-2 min-w-[100px] transition-colors shrink-0 " +
          (on
            ? "bg-sky-500 text-white border-sky-600 hover:bg-sky-600"
            : "bg-slate-100 text-slate-600 border-slate-300 hover:bg-slate-200")
        }
        title={
          on
            ? "ON — devices fetch the snapshot first on app start and show an Updating… pill while it loads. Falls back to the provider if the snapshot fails. Click to disable."
            : "OFF — devices fetch the provider's raw M3U on every cold start. Click to enable snapshot-first mode."
        }
      >
        {on ? "✓ ON" : "✗ OFF"}
      </button>
      <div className="text-[11px] text-slate-600 leading-snug">
        <span className="font-semibold text-slate-700">
          Use snapshot when opening the app
        </span>
        <div className="text-slate-500 mt-0.5">
          Devices fetch the pre-parsed snapshot first on cold start and show
          a small <em>“Updating playlist…”</em> pill while it loads. Falls
          back to the provider&apos;s raw M3U if the snapshot can&apos;t be
          downloaded.
        </div>
      </div>
    </div>
  );
}

function DisabledSection({ title, reason }) {
  return (
    <div className="opacity-60">
      <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">
        {title}
      </div>
      <div className="text-[11px] text-slate-500 italic">{reason}</div>
    </div>
  );
}

// v1.4.26 — Per-playlist toggle that gates the client-side Xtream
// `get_series` harvest step. Recommended for playlists with 200k+
// rows on Smart TVs — this is the last remaining OOM-heavy step on
// cold start after the v1.4.20 EPG / v1.4.24 IDB-wipe guards.
function DisableSeriesPreloadToggle({ on, onToggle, testId }) {
  return (
    <div className="flex items-start gap-3 p-2.5 rounded-md bg-amber-50/60 border border-amber-200 mt-2">
      <button
        type="button"
        data-testid={testId}
        onClick={() => onToggle(!on)}
        className={
          "text-[10px] font-bold px-2.5 py-1.5 rounded-md border-2 min-w-[100px] transition-colors shrink-0 " +
          (on
            ? "bg-amber-500 text-white border-amber-600 hover:bg-amber-600"
            : "bg-slate-100 text-slate-600 border-slate-300 hover:bg-slate-200")
        }
        title={
          on
            ? "ON — devices skip the Xtream get_series harvest on cold start. Prevents OOM on Smart TVs with 200k+ playlists. Click to disable."
            : "OFF — devices merge the full Xtream series catalog on cold start (default). Click to enable OOM-safe mode."
        }
      >
        {on ? "✓ ON" : "✗ OFF"}
      </button>
      <div className="text-[11px] text-slate-600 leading-snug">
        <span className="font-semibold text-slate-700">
          Disable series pre-loading (huge-catalog OOM guard)
        </span>
        <div className="text-slate-500 mt-0.5">
          Skip the Xtream <code className="text-[10px]">get_series</code>{" "}
          harvest on cold start. Recommended when Smart-TV users report
          crashes or the app hangs at &quot;Updating playlist…&quot;.
          Trade-off: series that exist only in the Xtream API (not the M3U)
          won&apos;t appear — typically 1–5 % of the library.
        </div>
      </div>
    </div>
  );
}

// v1.4.27 — Legacy M3U-first opt-out. Default is OFF, meaning devices
// build the channel list from the Xtream JSON API. Turn ON only when
// a provider's Xtream `get_live_streams` / `get_vod_streams` /
// `get_series` endpoints are broken but their M3U still works.
function PreferM3uSourceToggle({ on, onToggle, testId }) {
  return (
    <div className="flex items-start gap-3 p-2.5 rounded-md bg-sky-50/60 border border-sky-200 mt-2">
      <button
        type="button"
        data-testid={testId}
        onClick={() => onToggle(!on)}
        className={
          "text-[10px] font-bold px-2.5 py-1.5 rounded-md border-2 min-w-[100px] transition-colors shrink-0 " +
          (on
            ? "bg-sky-500 text-white border-sky-600 hover:bg-sky-600"
            : "bg-slate-100 text-slate-600 border-slate-300 hover:bg-slate-200")
        }
        title={
          on
            ? "ON — legacy M3U-first flow. Slower on huge catalogs. Click to revert to Xtream-API-first (recommended)."
            : "OFF — Xtream JSON API is used as the primary channel source (default & recommended). Click to force the legacy M3U-first flow."
        }
      >
        {on ? "✓ M3U" : "✓ XTREAM"}
      </button>
      <div className="text-[11px] text-slate-600 leading-snug">
        <span className="font-semibold text-slate-700">
          Prefer M3U source (legacy flow)
        </span>
        <div className="text-slate-500 mt-0.5">
          Default is <strong>OFF</strong> so devices use the Xtream JSON API
          (<code className="text-[10px]">get_live_streams</code>,{" "}
          <code className="text-[10px]">get_vod_streams</code>,{" "}
          <code className="text-[10px]">get_series</code>) as the primary
          channel source — faster and more complete on 400 k+ catalogs.
          Only turn ON when a provider&apos;s Xtream API is broken but
          their raw M3U still loads.
        </div>
      </div>
    </div>
  );
}

